import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { FilterGroup, InstrumentDataForPosition, InstrumentType, Position, PositionFilterType } from '@zero/shared/util-models';
import { PositionFilter } from './position-filter';

@Component({
  selector: 'web-components-position-filter',
  templateUrl: './position-filter.component.html',
  styleUrls: ['./position-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe],
})
export class PositionFilterComponent implements OnChanges {
  @Input() public positions?: (Position | InstrumentDataForPosition)[];
  @Input() public hasSavingsPlans?: boolean;
  @Input() public overrideCurrentFilter?: FilterGroup;

  public filters: PositionFilter[] = [];
  public isInitial = true;

  #currentFilter?: PositionFilter;
  #positionsCount?: number;

  readonly #cd: ChangeDetectorRef = inject(ChangeDetectorRef);

  @Output() public filterChanged = new EventEmitter<PositionFilter>();

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['positions']?.currentValue) {
      this.#positionsCount !== this.positions?.length && this.#createFilters();
    }
  }

  public activate = (ev: Event): void => {
    this.isInitial = false;
    const currentType = (ev.target as HTMLSelectElement).value;
    const filter = this.filters.find((f) => f.type === currentType);
    if (filter?.isActive && !this.overrideCurrentFilter) {
      return;
    }

    this.filters.forEach((f) => (f.isActive = false));
    filter.isActive = true;
    this.filterChanged.emit(filter);
    this.#currentFilter = filter;
  };

  #createFilters = (): void => {
    this.#positionsCount = this.positions?.length;
    if (!this.#hasDifferentTypes()) {
      this.filters = [];
      return;
    }

    const all = new PositionFilter(
      PositionFilterType.ALL,
      Boolean(!this.#currentFilter) || this.#currentFilter?.type === PositionFilterType.ALL,
    );
    const categories = new PositionFilter(
      PositionFilterType.CATEGORIES,
      Boolean(this.#currentFilter?.type === PositionFilterType.CATEGORIES),
    );
    this.filters = [all, categories];
    this.#hasType(InstrumentType.Share) && this.#addFilter(InstrumentType.Share);
    this.#hasType(InstrumentType.Etf) && this.#addFilter(InstrumentType.Etf);
    this.#hasType(InstrumentType.Funds) && this.#addFilter(InstrumentType.Funds);
    this.#hasType(InstrumentType.Derivative) && this.#addFilter(InstrumentType.Derivative);
    this.#hasType(InstrumentType.Crypto) && this.#addFilter(InstrumentType.Crypto);
    this.hasSavingsPlans && this.#addFilter(PositionFilterType.SAVINGS_PLAN);
    this.#cd.detectChanges();
  };

  #addFilter = (group: FilterGroup): number => this.filters.push(new PositionFilter(group, Boolean(this.#currentFilter?.type === group)));

  #hasType = (type: InstrumentType): boolean => this.positions?.some((position) => position.type === type);

  #hasDifferentTypes = (): boolean => {
    if (!this.positions) {
      return false;
    }

    const { length } = this.positions;
    if (!length || length === 1) {
      return false;
    }

    const types = [];
    for (const { type } of this.positions) {
      if (!types.includes(type)) {
        types.push(type);
      }
    }

    return types.length > 1;
  };
}
